import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { fmtMoney, initials, fmtTime } from "@/lib/helpers";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Users, Clock, PersonSimpleRun, CheckCircle, Coins, Receipt,
  Stethoscope, Baby, ArrowRight, Calendar, Warning
} from "@phosphor-icons/react";
import PlanUsageWidget from "@/components/PlanUsageWidget";
import DemoDataBanner from "@/components/DemoDataBanner";

import { devLog } from "@/lib/devLog";
const KPI_ICON = {
  students_present: Users,
  therapists_present: Stethoscope,
  waiting: Clock,
  in_session: PersonSimpleRun,
  completed_today: CheckCircle,
  total_students: Baby,
  revenue_today: Coins,
  outstanding_total: Receipt,
};

const KPI_LABEL = {
  students_present: "Students present",
  therapists_present: "Therapists in",
  waiting: "Waiting now",
  in_session: "In session",
  completed_today: "Sessions done today",
  total_students: "Active students",
  revenue_today: "Revenue today",
  outstanding_total: "Outstanding",
  sessions_today: "Sessions today",
  completed: "Completed",
  in_progress: "In progress",
  scheduled: "Scheduled",
  invoices_unpaid: "Unpaid",
  invoices_partial: "Partial",
  invoices_paid: "Paid",
};

function KPICard({ k, v, tone = "default" }) {
  const Icon = KPI_ICON[k] || Users;
  const isMoney = k.includes("revenue") || k.includes("outstanding");
  return (
    <Card className="hover:shadow-sm transition-shadow" data-testid={`kpi-${k}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div className="overline">{KPI_LABEL[k] || k}</div>
          <div className={`h-8 w-8 rounded-md flex items-center justify-center bg-primary/10 text-primary`}>
            <Icon size={16} weight="duotone" />
          </div>
        </div>
        <div className="mt-3 font-display text-3xl font-bold tracking-tight">
          {isMoney ? fmtMoney(v) : v}
        </div>
      </CardContent>
    </Card>
  );
}

export default function DashboardPage() {
  const { user, activeRole, activeBranch } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        const params = { role: activeRole };
        if (activeBranch) params.branch_id = activeBranch;
        const { data } = await api.get("/dashboard", { params });
        if (mounted) setData(data);
      } catch (e) { toast.error(formatApiError(e)); }
      finally { if (mounted) setLoading(false); }
    })();
    return () => { mounted = false; };
  }, [activeRole, activeBranch]);

  if (loading || !data) {
    return <div className="text-muted-foreground text-sm">Loading dashboard…</div>;
  }

  const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 17) return "Good afternoon";
    return "Good evening";
  };

  return (
    <div className="space-y-8">
      {/* Demo-data banner (Owner/Admin only, only when the workspace has demo rows) */}
      <DemoDataBanner />

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="overline mb-1.5">{activeRole} view</div>
          <h1 className="font-display text-3xl lg:text-4xl font-bold tracking-tight">
            {greeting()}, {user?.name?.split(" ")[0] || "there"}.
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Here&apos;s what&apos;s happening today across your therapy center.
          </p>
        </div>
        {["owner","admin","reception"].includes(activeRole) && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate("/attendance")} data-testid="quick-attendance" className="gap-2">
              <Calendar size={16} weight="duotone" /> Attendance
            </Button>
            <Button onClick={() => navigate("/queue")} data-testid="quick-queue" className="gap-2">
              <Clock size={16} weight="duotone" /> Live Queue
            </Button>
          </div>
        )}
      </div>

      {/* KPIs */}
      {data.kpis && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 stagger">
          {Object.entries(data.kpis).map(([k, v]) => (
            <KPICard key={k} k={k} v={v} />
          ))}
        </div>
      )}

      {/* Role-specific content */}
      {activeRole === "guardian" && (
        <GuardianView data={data} navigate={navigate} />
      )}

      {(["owner","admin","reception"].includes(activeRole)) && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2">
            <RecentSessions items={data.recent_sessions || []} navigate={navigate} />
          </div>
          {["owner","admin"].includes(activeRole) && (
            <div className="lg:col-span-1">
              <PlanUsageWidget />
            </div>
          )}
        </div>
      )}

      {activeRole === "therapist" && (
        <TherapistView data={data} navigate={navigate} />
      )}
    </div>
  );
}

function RecentSessions({ items, navigate }) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="font-display tracking-tight text-lg">Recent sessions</CardTitle>
          <p className="text-xs text-muted-foreground mt-0.5">Latest activity across all therapists</p>
        </div>
        <Button variant="ghost" size="sm" onClick={() => navigate("/sessions")} className="gap-1" data-testid="view-all-sessions">
          View all <ArrowRight size={14} />
        </Button>
      </CardHeader>
      <CardContent>
        {items.length === 0 ? (
          <div className="py-8 text-center text-sm text-muted-foreground">No sessions yet today.</div>
        ) : (
          <div className="divide-y divide-border">
            {items.map(s => (
              <div key={s.id} className="py-3 flex items-center gap-3">
                <div className={`h-2 w-2 rounded-full ${s.status==="in_progress" ? "bg-teal-500 animate-pulse" : s.status==="completed" ? "bg-primary" : "bg-amber-500"}`} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{s.student_ids?.length || 0} student(s) · {s.status}</div>
                  <div className="text-xs text-muted-foreground">{fmtTime(s.started_at || s.created_at)}</div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => navigate(`/sessions/${s.id}`)}>Open</Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function TherapistView({ data, navigate }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="font-display tracking-tight text-lg">Today&apos;s sessions</CardTitle>
      </CardHeader>
      <CardContent>
        {(!data.sessions_today || data.sessions_today.length === 0) ? (
          <div className="py-8 text-center text-sm text-muted-foreground">No sessions assigned yet.</div>
        ) : (
          <div className="grid gap-3">
            {data.sessions_today.map(s => (
              <div key={s.id} className="flex items-center gap-3 p-3 border border-border rounded-md hover:bg-muted/50">
                <div className={`h-9 w-9 rounded-md flex items-center justify-center text-xs font-semibold ${s.status==="in_progress" ? "bg-teal-100 text-teal-800" : s.status==="completed" ? "bg-primary/10 text-primary" : "bg-amber-100 text-amber-800"}`}>
                  {s.status.slice(0,2).toUpperCase()}
                </div>
                <div className="flex-1 text-sm">
                  <div className="font-medium">{s.student_ids?.length} student(s)</div>
                  <div className="text-xs text-muted-foreground">{s.status} · {fmtTime(s.started_at || s.created_at)}</div>
                </div>
                <Button size="sm" onClick={() => navigate(`/sessions/${s.id}`)}>Open</Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function GuardianView({ data, navigate }) {
  const [progressByChild, setProgressByChild] = useState({});
  useEffect(() => {
    let mounted = true;
    (async () => {
      const map = {};
      for (const c of (data.children || [])) {
        try {
          const { data: p } = await api.get(`/students/${c.id}/progress`);
          map[c.id] = p;
        } catch (err) { devLog("progress fetch failed", err); }
      }
      if (mounted) setProgressByChild(map);
    })();
    return () => { mounted = false; };
  }, [data]);

  return (
    <div className="space-y-6">
      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="font-display tracking-tight text-lg">Your children</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {(data.children || []).length === 0 && <div className="text-sm text-muted-foreground">No children linked yet.</div>}
            {(data.children || []).map(c => (
              <div key={c.id} className="flex items-center gap-3 p-3 border border-border rounded-md">
                <Avatar className="h-10 w-10"><AvatarFallback className="bg-accent/20 text-accent">{initials(c.name)}</AvatarFallback></Avatar>
                <div className="flex-1"><div className="font-medium text-sm">{c.name}</div><div className="text-xs text-muted-foreground">{c.diagnosis || "—"}</div></div>
                <Button size="sm" variant="outline" onClick={() => navigate(`/students/${c.id}`)}>View</Button>
              </div>
            ))}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="font-display tracking-tight text-lg">Recent sessions</CardTitle>
              {data.outstanding > 0 && (
                <Badge className="bg-amber-100 text-amber-800 border-amber-200 gap-1">
                  <Warning size={12} weight="fill" /> {fmtMoney(data.outstanding)} due
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {(data.recent_sessions || []).length === 0 && <div className="text-sm text-muted-foreground">No sessions yet.</div>}
            {(data.recent_sessions || []).slice(0, 6).map(s => (
              <div key={s.id} className="flex items-center gap-3 p-2 hover:bg-muted/50 rounded">
                <CheckCircle size={16} className="text-primary" weight="duotone" />
                <div className="flex-1 text-sm">{fmtTime(s.ended_at)}</div>
                <span className="text-xs text-muted-foreground">Completed</span>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      {(data.children || []).map(c => (
        <ChildProgressCard key={c.id} child={c} progress={progressByChild[c.id]} />
      ))}
    </div>
  );
}

function ChildProgressCard({ child, progress }) {
  if (!progress) return null;
  const entries = progress.entries || [];
  const byTherapy = progress.by_therapy || [];
  if (entries.length === 0) return null;

  // Build combined trend: {date, rating_OT, rating_ST, ...}
  const dateMap = {};
  const codes = new Set();
  for (const e of entries) {
    const dm = dateMap[e.date] || { date: e.date };
    dm[e.therapy_code] = e.rating;
    codes.add(e.therapy_code);
    dateMap[e.date] = dm;
  }
  const chartData = Object.values(dateMap).sort((a, b) => a.date.localeCompare(b.date));

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="font-display tracking-tight text-lg">{child.name}'s progress</CardTitle>
            <p className="text-xs text-muted-foreground mt-0.5">{entries.length} completed sessions</p>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {byTherapy.map(t => (
              <div key={t.therapy_id} className="text-xs flex items-center gap-1.5 border border-border rounded-md px-2 py-1">
                <span className="h-2 w-2 rounded-full" style={{ backgroundColor: t.therapy_color }} />
                <span className="font-medium">{t.therapy_name}</span>
                <span className="text-muted-foreground">· avg {t.avg_rating}/5</span>
              </div>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E2DC" />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#5C5C5C" }} />
              <YAxis domain={[0, 5]} tick={{ fontSize: 11, fill: "#5C5C5C" }} />
              <Tooltip contentStyle={{ background: "#fff", border: "1px solid #E5E2DC", borderRadius: 6, fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              {byTherapy.map(t => (
                <Line
                  key={t.therapy_id}
                  type="monotone"
                  dataKey={progress.entries.find(e => e.therapy_id === t.therapy_id)?.therapy_code}
                  stroke={t.therapy_color}
                  strokeWidth={2.5}
                  dot={{ r: 4, fill: t.therapy_color }}
                  connectNulls
                  name={t.therapy_name}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}
